import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { GET } from '../../Screens/dataSaver';
import { FormatPhoneNumber } from '../../utils/formatting';
import style from './index.module.scss';

const isMedicalLearnerForm = () => {
  const path = window.location.pathname;
  return path.startsWith('/learner') || path.includes('/medLearner');
};

export const isLearnerLookupField = (fieldKey, baseKey) => {
  if (!isMedicalLearnerForm()) return false;
  const key = String(baseKey || '');
  const onDepartmentSpecialty = key === 'departmentSpecialty' || key.endsWith('.departmentSpecialty');
  return onDepartmentSpecialty && (fieldKey === 'learnerYear' || fieldKey === 'cmhSupervisorId');
};

export const isLearnerNameSearchField = (fieldKey, baseKey) => {
  if (!window.location.pathname.startsWith('/learner/create')) return false;
  const key = String(baseKey || '');
  return fieldKey === 'firstName' && (key === 'learner' || key.endsWith('.learner'));
};

const asNameText = (value) => (value == null ? '' : String(value).trim());

const splitDisplayName = (name) => {
  const parts = asNameText(name).split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: '', middleName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], middleName: '', lastName: '' };
  return {
    firstName: parts[0],
    middleName: parts.length > 2 ? parts.slice(1, -1).join(' ') : '',
    lastName: parts[parts.length - 1],
  };
};

export const learnerIdentityFromRecord = (record) => {
  const nameObject = record?.name && typeof record.name === 'object' ? record.name : null;
  const split = splitDisplayName(typeof record?.name === 'string' ? record.name : '');
  const phone = FormatPhoneNumber(asNameText(
    record?.cellPhone || record?.mobileNumber || record?.phone || ''
  )) || '';
  return {
    firstName: asNameText(record?.firstName || nameObject?.firstName) || split.firstName,
    middleName: asNameText(record?.middleName || nameObject?.middleName) || split.middleName,
    lastName: asNameText(record?.lastName || nameObject?.lastName) || split.lastName,
    email: asNameText(record?.email),
    cellPhone: phone,
    mobileNumber: phone,
  };
};

const learnerDisplayName = (record) => {
  if (typeof record?.name === 'string' && record.name.trim()) return record.name.trim();
  const identity = learnerIdentityFromRecord(record);
  return [identity.firstName, identity.middleName, identity.lastName].filter(Boolean).join(' ');
};

export const LearnerNameSearchField = ({
  label,
  required,
  value,
  onChange,
  onSelect,
  placeholder,
  warning,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    const query = searchText.trim();
    if (query.length < 2) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      const params = new URLSearchParams({
        searchText: query,
        limit: '20',
        offset: '0',
      });
      const queryString = params.toString();
      Promise.all([
        GET(`learner-management-service/medicalLearner/learners/active?${queryString}`, { signal: controller.signal }),
        GET(`learner-management-service/medicalLearner/learners/inactive?${queryString}`, { signal: controller.signal }),
      ])
        .then(([activeResponse, inactiveResponse]) => {
          if (controller.signal.aborted) return;
          const active = Array.isArray(activeResponse?.data?.data) ? activeResponse.data.data : [];
          const inactive = Array.isArray(inactiveResponse?.data?.data) ? inactiveResponse.data.data : [];
          const combined = new Map();
          active.forEach((item) => {
            if (item?.id) combined.set(item.id, { ...item, roster: 'Active' });
          });
          inactive.forEach((item) => {
            if (item?.id && !combined.has(item.id)) combined.set(item.id, { ...item, roster: 'Inactive' });
          });
          setOptions([...combined.values()]);
        })
        .catch(() => {
          if (!controller.signal.aborted) setOptions([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchText]);

  return (
    <div>
      <FieldLabel label={label} required={required} />
      <Autocomplete
        freeSolo
        options={options}
        inputValue={value || ''}
        filterOptions={(items) => items}
        getOptionLabel={(option) => (typeof option === 'string' ? option : learnerDisplayName(option))}
        onInputChange={(event, nextValue, reason) => {
          if (reason === 'input') {
            onChange(nextValue || '');
            setSearchText(nextValue || '');
          }
          if (reason === 'clear') {
            onChange('');
            setSearchText('');
          }
        }}
        onChange={(event, nextValue) => {
          if (nextValue && typeof nextValue === 'object') onSelect(learnerIdentityFromRecord(nextValue));
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            error={Boolean(warning)}
            placeholder={placeholder || 'Enter First Name'}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <div>
              <div>{learnerDisplayName(option)}</div>
              <div>
                {[option.email, option.roster].filter(Boolean).join(' · ')}
              </div>
            </div>
          </li>
        )}
      />
    </div>
  );
};

const personName = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  return [value.firstName, value.middleName, value.lastName].filter(Boolean).join(' ');
};

export const LearnerSupervisorName = ({ value, department, specialty }) => {
  const [name, setName] = useState('');

  useEffect(() => {
    if (!value) {
      setName('');
      return undefined;
    }
    let cancelled = false;
    const params = new URLSearchParams({ limit: '50' });
    if (department) params.set('department', String(department));
    if (specialty) params.set('specialty', String(specialty));
    const supervisorName = (supervisor) => personName(supervisor?.displayName) || personName(supervisor?.name);
    const loadName = async () => {
      try {
        const { data } = await GET(`learner-management-service/medicalLearner/creation/supervisors?${params.toString()}`);
        const match = (Array.isArray(data?.items) ? data.items : []).find((item) => item?.id === value);
        const resolved = supervisorName(match);
        if (resolved) return resolved;
      } catch (error) {
        // Fall through to the user record when the supervisor list does not include this id.
      }
      try {
        const { data: user } = await GET(`user-management-service/user/${value}`);
        return personName(user?.name) || personName(user?.displayName) || '';
      } catch (error) {
        return '';
      }
    };
    loadName().then((resolved) => {
      if (!cancelled) setName(resolved || '');
    });
    return () => {
      cancelled = true;
    };
  }, [value, department, specialty]);

  return name || '-';
};

const FieldLabel = ({ label, required }) => (
  <div className={style.lableStyle}>
    {label}
    {required ? '*' : ''}
  </div>
);

const LearnerLookupField = ({
  kind,
  label,
  required,
  value,
  onChange,
  learnerType,
  department,
  specialty,
  disabled,
  warning,
}) => {
  const [yearOptions, setYearOptions] = useState([]);
  const [allowCustomYear, setAllowCustomYear] = useState(true);
  const [supervisors, setSupervisors] = useState([]);
  const [supervisorSearch, setSupervisorSearch] = useState('');
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const supervisorScope = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (kind !== 'learnerYear') return undefined;
    if (!learnerType) {
      setYearOptions([]);
      setAllowCustomYear(true);
      return undefined;
    }
    let cancelled = false;
    const params = new URLSearchParams({ learnerType: String(learnerType) });
    GET(`learner-management-service/medicalLearner/creation/learnerYears?${params.toString()}`)
      .then(({ data }) => {
        if (cancelled) return;
        setYearOptions(Array.isArray(data?.options) ? data.options : []);
        setAllowCustomYear(data?.allowCustom !== false);
      })
      .catch(() => {
        if (!cancelled) {
          setYearOptions([]);
          setAllowCustomYear(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [kind, learnerType]);

  useEffect(() => {
    if (kind !== 'cmhSupervisorId') return undefined;
    const scope = `${department || ''}|${specialty || ''}`;
    const [previousDepartment] = String(supervisorScope.current || '|').split('|');
    if (supervisorScope.current && previousDepartment && supervisorScope.current !== scope) {
      onChangeRef.current('');
      setSelectedSupervisor(null);
      setSupervisorSearch('');
    }
    supervisorScope.current = scope;
    return undefined;
  }, [kind, department, specialty]);

  useEffect(() => {
    if (kind !== 'cmhSupervisorId') return undefined;
    if (!department) {
      setSupervisors([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      const params = new URLSearchParams({
        department: String(department),
        limit: '50',
      });
      if (specialty) params.set('specialty', String(specialty));
      if (supervisorSearch.trim()) params.set('search', supervisorSearch.trim());
      GET(`learner-management-service/medicalLearner/creation/supervisors?${params.toString()}`)
        .then(({ data }) => {
          if (!cancelled) setSupervisors(Array.isArray(data?.items) ? data.items : []);
        })
        .catch(() => {
          if (!cancelled) setSupervisors([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [kind, department, specialty, supervisorSearch]);

  useEffect(() => {
    if (kind !== 'cmhSupervisorId' || !value) return;
    const match = supervisors.find((item) => item?.id === value);
    if (match) setSelectedSupervisor(match);
  }, [kind, supervisors, value]);

  if (kind === 'learnerYear') {
    const options = value && !yearOptions.includes(value) ? [value, ...yearOptions] : yearOptions;
    return (
      <div>
        <FieldLabel label={label} required={required} />
        <Autocomplete
          freeSolo={allowCustomYear}
          options={options}
          value={value || null}
          disabled={disabled || !learnerType}
          onChange={(event, nextValue) => onChange(nextValue || '')}
          onInputChange={(event, nextValue, reason) => {
            if (allowCustomYear && reason === 'input') onChange(nextValue || '');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              error={Boolean(warning)}
              placeholder={learnerType ? 'Select learner year' : 'Select a learner type first'}
            />
          )}
        />
      </div>
    );
  }

  const selected = selectedSupervisor?.id === value
    ? selectedSupervisor
    : (supervisors.find((item) => item?.id === value) || (value ? { id: value, displayName: value } : null));
  const supervisorReady = Boolean(department);

  return (
    <div>
      <FieldLabel label={label} required={required} />
      <Autocomplete
        options={supervisors}
        value={selected}
        disabled={disabled || !supervisorReady}
        filterOptions={(options) => options}
        getOptionLabel={(option) => option?.displayName || option?.name || option?.staffId || ''}
        isOptionEqualToValue={(option, current) => option?.id === current?.id}
        onChange={(event, nextValue) => {
          setSelectedSupervisor(nextValue);
          onChange(nextValue?.id || '');
        }}
        onInputChange={(event, nextValue, reason) => {
          if (reason === 'input') setSupervisorSearch(nextValue || '');
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            error={Boolean(warning)}
            placeholder={supervisorReady ? 'Search supervisor by name or staff ID' : 'Select a department first'}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.displayName || option.name}
          </li>
        )}
      />
    </div>
  );
};

export default LearnerLookupField;
